import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import IconButton from "../../../../packages/IconButton";
import MoveRightLastPageIcon from "../../../../packages/SVGs/MoveRightLastPageIcon";
import WPButton from "../../../../packages/WPButton";
import WPHeading from "../../../../packages/WPHeading";

import LicenseFilterPills from "./LicenseFilterPills/LicenseFilterPills";
import {
  calculateTotalFilters,
  formatDateRange,
  getActiveStatuses,
  getLicenseName,
} from "./LicenseFilter.utils";

import { DATE_FORMATS } from "../../../../constants/global.constants";
import {
  NO_FILTERS_APPLIED,
  STATUS_FILTER_ICONS,
} from "../../../../constants/LicenseManagement.constants";

import { CalendarIcon,RemoveIcon, SearchIcon } from "../../../../assets/icons";

import "./LicenseAppliedFilters.scss";

const LicenseAppliedFilters = ({
  licensePayload,
  licensePermissions,
  onFilterUpdate,
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const {
    licenses = [],
    companyName = "",
    searchstr = "",
    fromDate,
    toDate,
  } = licensePayload;

  const totalFilters = useMemo(
    () =>
      calculateTotalFilters(
        licenses,
        companyName,
        { fromDate, toDate },
        searchstr,
      ),
    [licenses, companyName, fromDate, toDate, searchstr],
  );

  /**
   * Updates visibility of left and right scroll arrows based on current scroll position.
   * Shows left arrow if container has scrolled, shows right arrow if more content exists.
   */
  const updateScrollArrowVisibility = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1,
    );
  }, []);

  /**
   * Handles left scroll button click.
   * Scrolls container left and updates arrow visibility.
   */
  const handleScrollLeft = useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
    setTimeout(updateScrollArrowVisibility, 300);
  }, [updateScrollArrowVisibility]);

  /**
   * Handles right scroll button click.
   * Scrolls container right and updates arrow visibility.
   */
  const handleScrollRight = useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
    setTimeout(updateScrollArrowVisibility, 300);
  }, [updateScrollArrowVisibility]);

  const getStatusIconByKey = useCallback(
    (status) => STATUS_FILTER_ICONS[status] || null,
    [],
  );

  const renderCombinedIcons = useCallback(
    (activeStatuses) => (
      <span className="filter-pill-icons">
        {activeStatuses.map(({ key, type }) => {
          const icon = getStatusIconByKey(type);
          return icon && <span key={key}>{icon}</span>;
        })}
      </span>
    ),
    [getStatusIconByKey],
  );

  /**
   * Creates a memoized handler for removing all statuses of a specific license.
   * Optimized to filter and update in a single pass using reduce.
   */
  const createRemoveAllStatusesHandler = useCallback(
    (activeStatuses, licenseId) => () => {
      const statusKeys = activeStatuses.map((s) => s.key);
      if (statusKeys.length === 0) return;

      const statusUpdates = Object.fromEntries(
        statusKeys.map((key) => [key, false]),
      );
      const cleanedFilters = licenses.reduce((acc, filter) => {
        const updatedFilter =
          filter.licenseId === licenseId
            ? { ...filter, ...statusUpdates }
            : filter;
        if (
          updatedFilter.isActive ||
          updatedFilter.isExpiringSoon ||
          updatedFilter.isExpired ||
          updatedFilter.isTerminated ||
          updatedFilter.isUnassigned
        ) {
          acc.push(updatedFilter);
        }

        return acc;
      }, []);

      onFilterUpdate({ licenses: cleanedFilters });
    },
    [licenses, onFilterUpdate],
  );

  /**
   * Generic handler to clear a single filter field.
   */
  const createSingleFilterRemovalHandler = useCallback(
    (filterUpdates) => () => onFilterUpdate(filterUpdates),
    [onFilterUpdate],
  );

  /**
   * Formatted date range label for display.
   */
  const formattedDateRangeLabel = useMemo(
    () =>
      fromDate && toDate
        ? formatDateRange(fromDate, toDate, (date) =>
            dayjs(date).format(DATE_FORMATS.MMM_DD_YYYY),
          )
        : "",
    [fromDate, toDate],
  );

  useEffect(() => {
    updateScrollArrowVisibility();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", updateScrollArrowVisibility);
      return () =>
        container.removeEventListener("scroll", updateScrollArrowVisibility);
    }
  }, [updateScrollArrowVisibility, totalFilters]);

  if (totalFilters === 0) return null;

  return (
    <div className="applied-filters-container">
      <div className="applied-filters-content">
        <WPHeading
          size="small"
          text={t("licenseManagement.filtersApplied")}
          className="applied-filter-text"
        />
        {showLeftArrow && (
          <IconButton
            className="scroll-arrow"
            onClick={handleScrollLeft}
            aria-label="Scroll left"
            id="scroll-arrow-left"
          >
            <MoveRightLastPageIcon className="scroll-arrow-icon scroll-arrow-icon-left" />
          </IconButton>
        )}

        <div className="filters-scroll-container" ref={scrollContainerRef}>
          <div className="filters-list">
            {searchstr && (
              <LicenseFilterPills
                icon={<SearchIcon />}
                label={searchstr}
                onRemove={createSingleFilterRemovalHandler({ searchstr: "" })}
                dataTestId="search-filter-pill"
              />
            )}

            {licenses?.map((filter) => {
              const activeStatuses = getActiveStatuses(filter);
              if (activeStatuses.length === 0) return null;

              return (
                <LicenseFilterPills
                  key={filter.licenseId}
                  icon={renderCombinedIcons(activeStatuses)}
                  label={getLicenseName(filter.licenseId, licensePermissions)}
                  onRemove={createRemoveAllStatusesHandler(
                    activeStatuses,
                    filter.licenseId,
                  )}
                  dataTestId={`license-status-${filter.licenseId}`}
                />
              );
            })}

            {companyName && (
              <LicenseFilterPills
                icon={<SearchIcon />}
                label={companyName}
                onRemove={createSingleFilterRemovalHandler({ companyName: "" })}
                dataTestId="company-filter-pill"
              />
            )}

            {fromDate && toDate && (
              <LicenseFilterPills
                icon={<CalendarIcon fill="var(--du-color-black)" />}
                label={formattedDateRangeLabel}
                onRemove={createSingleFilterRemovalHandler({
                  fromDate: null,
                  toDate: null,
                })}
                dataTestId="date-range-filter-pill"
              />
            )}
          </div>
        </div>

        {showRightArrow && (
          <IconButton
            className="scroll-arrow"
            onClick={handleScrollRight}
            aria-label="Scroll right"
            id="scroll-arrow-right"
          >
            <MoveRightLastPageIcon className="scroll-arrow-icon" />
          </IconButton>
        )}

        <WPButton
          variant="text"
          onClick={createSingleFilterRemovalHandler(NO_FILTERS_APPLIED)}
          customClass="clear-all-button"
          id="clear-all-button"
        >
          {t("licenseManagement.clearAll")}
        </WPButton>
        <IconButton
          className="filter-pill-close"
          onClick={createSingleFilterRemovalHandler(NO_FILTERS_APPLIED)}
          aria-label="Clear all filters"
          id="clear-all-close-icon"
        >
          <RemoveIcon />
        </IconButton>
      </div>
    </div>
  );
};

export default LicenseAppliedFilters;
